<template>
  <div class="pricing-page">

    <!-- 🌐 Global Loader -->
    <transition name="fade-slide">
      <div v-if="loading" class="global-loader">
        <div class="loader-spinner"></div>
        <p>{{ loadingText }}</p>
      </div>
    </transition>

    <!-- === FILTER ROW === -->
    <div class="filter-row"
         style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap;
                gap:12px; margin:0 .5rem 1.5rem .5rem; animation:fadeIn 0.4s ease;">
      <h1 style="display:flex; align-items:center; gap:8px; font-size:1.2rem; font-weight:700; color:var(--text); margin:0;">
        <i class="bx bx-credit-card" style="font-size:1.4rem; color:var(--primary);"></i>
        Pricing & Plans
      </h1>

      <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
        <select v-model="selectedStatus"
                style="padding:8px 12px; border:1px solid var(--border); border-radius:6px;
                       background:var(--card); color:var(--text); font-size:13px; min-width:140px;">
          <option>All Status</option>
          <option>Active</option>
          <option>Deactive</option>
        </select>

        <button @click="openAddPlan"
                style="display:inline-flex; align-items:center; gap:6px; background:var(--primary);
                       color:#fff; border:none; border-radius:6px; padding:8px 16px;
                       font-size:13px; font-weight:600; cursor:pointer; transition:all 0.3s ease;">
          <i class="bx bx-plus"></i> New Plan
        </button>
      </div>
    </div>

    <!-- === KPI ROW === -->
    <div class="kpi-row" style="display:flex; flex-wrap:wrap; gap:20px; margin-bottom:24px;">
      <div v-for="card in planStats" :key="card.title"
           class="kpi-card"
           style="flex:1; min-width:220px; background:var(--card); border:1px solid var(--border);
                  border-radius:var(--radius); padding:16px 20px; display:flex;
                  justify-content:space-between; align-items:center; box-shadow:0 2px 6px rgba(0,0,0,0.05);
                  transition:all 0.3s ease;">
        <div>
          <h4 style="margin:0; font-size:14px; color:var(--muted); font-weight:500;">{{ card.title }}</h4>
          <div style="font-size:1.4rem; font-weight:700; color:var(--text);">{{ card.value }}</div>
        </div>
        <i :class="card.icon" style="font-size:1.6rem; color:var(--primary); opacity:0.9;"></i>
      </div>
    </div>

    <!-- === PLAN CARDS GRID === -->
    <div class="plan-grid"
         style="display:flex; flex-wrap:wrap; gap:20px; justify-content:flex-start; animation:fadeIn 0.4s ease;">

      <!-- Empty State -->
      <div v-if="!plans.length" class="empty-state" style="margin:auto; text-align:center;">
        <i class="bx bx-package" style="font-size:2rem; color:var(--muted);"></i>
        <p style="margin-top:6px; color:var(--muted); font-size:14px;">No plans available.</p>
      </div>

      <!-- Plan Cards -->
      <div v-else
           v-for="plan in plans"
           :key="plan.id"
           class="plan-card"
           style="flex:0 0 calc(33.333% - 20px); background:var(--card); border:1px solid var(--border);
                  border-radius:var(--radius); box-shadow:0 2px 6px rgba(0,0,0,0.05); padding:16px;
                  display:flex; flex-direction:column; justify-content:space-between;
                  transition:all 0.3s ease; min-width:260px;">
        
        <!-- === Plan Header === -->
        <div>
          <div style="display:flex; align-items:center; justify-content:space-between;">
            <h4 style="margin:0; font-size:1rem; font-weight:600; color:var(--text);">{{ plan.name }}</h4>
            <span :class="'status ' + plan.status.toLowerCase()"
                  style="padding:4px 10px; border-radius:6px; font-size:0.8rem; font-weight:600;">
              {{ plan.status }}
            </span>
          </div>

          <p style="margin-top:8px; font-size:13px; color:var(--muted); line-height:1.5;">
            {{ plan.description }}
          </p>
        </div>

        <!-- === Plan Details === -->
        <div style="margin-top:12px;">
          <div style="display:flex; justify-content:space-between; align-items:center; font-size:12px; color:var(--muted);">
            <span>Duration: <strong>{{ plan.duration }}</strong></span>
            <span>Type: <strong>{{ plan.type }}</strong></span>
          </div>
          <div style="display:flex; justify-content:space-between; align-items:center; margin-top:6px; font-size:12px; color:var(--muted);">
            <span>Website: <strong>{{ plan.website }}</strong></span>
            <span>Domain: <strong style="color:var(--primary);">{{ plan.domain }}</strong></span>
          </div>
        </div>

        <!-- === Features List === -->
        <ul style="margin-top:12px; list-style:none; padding:0;">
          <li v-for="(f, i) in plan.features" :key="i"
              style="font-size:13px; color:var(--text); display:flex; align-items:center; gap:6px; margin-bottom:4px;">
            <i class="bx bx-check-circle" style="color:var(--primary); font-size:1rem;"></i>{{ f }}
          </li>
        </ul>

        <!-- === Footer Buttons === -->
        <div class="plan-footer"
             style="display:flex; justify-content:space-between; align-items:center; margin-top:14px;">
          <span style="font-size:12px; color:var(--muted);">Updated: {{ plan.updated }}</span>
          <div style="display:flex; gap:8px;">
            <button @click="openEditPlan(plan)" class="btn-edit">
              <i class="bx bx-edit-alt"></i> Edit
            </button>
            <button @click="deletePlan(plan)" class="btn-delete">
              <i class="bx bx-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- === PAGINATION === -->
    <div
      style="display:flex; justify-content:space-between; align-items:center; margin-top:24px; flex-wrap:wrap; gap:10px;"
    >
      <span style="font-size:13px; color:var(--muted);">
        Showing Page {{ currentPage }} of {{ totalPages }}
      </span>

      <div style="display:flex; align-items:center; gap:6px;">
        <button
          @click="prevPage"
          :disabled="currentPage === 1"
          style="border:none; background:#F3F4F6; color:var(--text); padding:6px 10px;
                 border-radius:6px; cursor:pointer;"
        >
          <i class="bx bx-chevron-left"></i>
        </button>

        <button
          v-for="page in totalPages"
          :key="page"
          @click="goToPage(page)"
          :class="{ active: currentPage === page }"
          style="border:none; padding:6px 10px; border-radius:6px;
                 background:var(--primary); color:#fff; font-weight:600;"
        >
          {{ page }}
        </button>

        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          style="border:none; background:#F3F4F6; color:var(--text); padding:6px 10px;
                 border-radius:6px; cursor:pointer;"
        >
          <i class="bx bx-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- 🧩 Add / Edit Plan Modal -->
<transition name="fade-slide">
  <div v-if="showPlanModal" class="modal-overlay" @click.self="closePlanModal">
    <div class="modal-card">
      <h2 style="display:flex; align-items:center; gap:8px; margin-bottom:14px;">
        <i class="bx bx-layer"></i> {{ editMode ? "Edit Plan" : "Add New Plan" }}
      </h2>

      <div class="form-grid">
        <div class="form-field">
          <label>Plan Title</label>
          <input v-model="planForm.title" type="text" placeholder="Enter plan name" />
        </div>

        <div class="form-field">
          <label>Price</label>
          <input v-model="planForm.price" type="number" placeholder="Enter price" />
        </div>

        <div class="form-field">
          <label>Duration</label>
          <select v-model="planForm.duration">
            <option>Monthly</option>
            <option>Quarterly</option>
            <option>Yearly</option>
          </select>
        </div>

        

        <div class="form-field full">
          <label>Description</label>
          <textarea v-model="planForm.desc" rows="3" placeholder="Short plan description..."></textarea>
        </div>

        <div class="form-field">
          <label>Plan Type</label>
          <input v-model="planForm.type" type="text" placeholder="Basic / Pro / Enterprise" />
        </div>

        <div class="form-field full">
          <label>Features (comma-separated)</label>
          <input v-model="planForm.features" type="text" placeholder="Ex: 1 Website, 5GB Storage" />
        </div>

        <div class="form-field full">
          <label>Upload Plan Image</label>
          <input type="file" @change="handleFileUpload" />
        </div>
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <button @click="closePlanModal" class="btn-cancel">Cancel</button>
        <button @click="savePlan" class="btn-save">
          <i class="bx bx-save"></i> {{ editMode ? "Update" : "Create" }}
        </button>
      </div>
    </div>
  </div>
</transition>

  </div>
</template>

<script>
import { getPlanList, getPlanReport, deletePlan, createPlan, getPlanById } from "@/apis/planapi";

export default {
  name: "PricingPage",

  data() {
    return {
      showPlanModal: false,
      editMode: false,
      planForm: {
        id: "",
        title: "",
        price: "",
        duration: "",
        desc: "",
        curr: "INR",
        inv: "",
        client: "",
        type: "",
        features: "",
        img: null,
      },
      selectedStatus: "All Status",
      currentPage: 0,
      perPage: 10,

      // Loaders
      loading: false,
      loadingText: "Loading plans...",
      loadingPlans: false,

      // Data
      plans: [],
      totalPlansCount: 0,
      totalPages: 0,


      planStats: [
        { title: "Total Plans", value: "—", icon: "bx bx-layer" },
        { title: "Active Subscribers", value: "—", icon: "bx bx-check-circle" },
        { title: "Free Users", value: "—", icon: "bx bx-user-circle" },
      ],
    };
  },

  computed: {
    /** Client-side filtering by status (if user filters) */
    filteredPlans() {
      if (this.selectedStatus === "All Status") return this.plans;
      return this.plans.filter((p) => p.status === this.selectedStatus);
    },
  },

  mounted() {
    this.initData();
  },

  methods: {
    /** 🌐 Initialize dashboard (fetch data) */
     openAddPlan() {
      this.editMode = false;
      this.resetPlanForm();
      this.showPlanModal = true;
    },
    async openEditPlan(plan) {
      this.editMode = true;
      this.loading = true;
      this.loadingText = "Loading plan details...";
      try {
        const res = await getPlanById(plan.id);
        if (res.status && res.plan_dt) {
          const p = res.plan_dt;
          this.planForm = {
            id: p.plan_id,
            title: p.plan_title,
            price: p.plan_price,
            duration: p.plan_duration,
            desc: p.plan_desc,
            curr: p.plan_curr,
            inv: p.plan_inv,
            client: p.plan_client,
            type: p.plan_type,
            features: p.plan_features ? JSON.parse(p.plan_features).join(", ") : "",
            img: null,
          };
          this.showPlanModal = true;
        }
      } catch (err) {
        console.error("openEditPlan error:", err);
      } finally {
        this.loading = false;
      }
    },

    closePlanModal() {
      this.showPlanModal = false;
    },

    handleFileUpload(e) {
      this.planForm.img = e.target.files[0];
    },

    resetPlanForm() {
      this.planForm = {
        id: "",
        title: "",
        price: "",
        duration: "",
        desc: "",
        curr: "INR",
        inv: "",
        client: "",
        type: "",
        features: "",
        img: null,
      };
    },

    /** 💾 Save or Update Plan */
    async savePlan() {
      if (!this.planForm.title) {
        alert("Please fill in all required fields.");
        return;
      }

      this.loading = true;
      this.loadingText = this.editMode ? "Updating plan..." : "Creating plan...";

      try {
        const res = await createPlan(
          this.planForm.id,
          this.planForm.title,
          this.planForm.price,
          this.planForm.duration,
          this.planForm.desc,
          this.planForm.curr,
          this.planForm.inv,
          this.planForm.client,
          this.planForm.type,
          JSON.stringify(this.planForm.features.split(",")),
          this.planForm.img
        );

        if (res.status) {
          this.showPlanModal = false;
          await this.fetchPlanList(this.currentPage);
          await this.fetchPlanReport();
        } else {
          alert(res.success || "Failed to save plan");
        }
      } catch (err) {
        console.error("savePlan error:", err);
      } finally {
        this.loading = false;
      }
    },
    async initData() {
      this.loading = true;
      this.loadingText = "Fetching plans and report...";
      try {
        await Promise.all([this.fetchPlanList(this.currentPage), this.fetchPlanReport()]);
      } finally {
        this.loading = false;
      }
    },

    /** 📦 Fetch paginated plans */
    async fetchPlanList(page = 0) {
      this.loadingPlans = true;
      this.loadingText = "Loading plan list...";
      try {
        const userData = JSON.parse(sessionStorage.getItem("ldata"));
        const res = await getPlanList(userData.user_id+"", "", page, ""); // user_id left blank intentionally
        if (res.status && Array.isArray(res.records)) {
          this.plans = res.records.map((p) => ({
            id: p.plan_id,
            name: p.plan_title,
            description: p.plan_desc || "No description provided.",
            duration: p.plan_duration || "—",
            type: p.plan_type || "—",
            features: p.plan_features ? JSON.parse(p.plan_features) : [],
            website: p.plan_client || "—",
            domain: p.plan_inv || "—",
            status: p.plan_status || "Active",
            updated: p.plan_datetime
              ? new Date(p.plan_datetime).toLocaleDateString()
              : "—",
          }));

          this.totalPlansCount = res.total1 ?? this.plans.length;
          this.totalPages = Math.ceil(this.totalPlansCount / 10); // server pagination (10 per page)
        } else {
          this.plans = [];
          this.totalPlansCount = 0;
        }
      } catch (err) {
        console.error("fetchPlanList error:", err);
      } finally {
        this.loadingPlans = false;
      }
    },

    /** 📊 Fetch KPI report */
    async fetchPlanReport() {
      try {
        const res = await getPlanReport();
        if (res.status && res.report) {
          this.planStats = [
            { title: "Total Plans", value: res.report.total_plans ?? 0, icon: "bx bx-layer" },
            { title: "Active Subscribers", value: res.report.active_subscribers ?? 0, icon: "bx bx-check-circle" },
            { title: "Free Users", value: res.report.free_users ?? 0, icon: "bx bx-user-circle" },
          ];
        }
      } catch (err) {
        console.error("fetchPlanReport error:", err);
      }
    },

    /** 🔍 Apply filters */
    applyFilters() {
      this.currentPage = 1;
      this.fetchPlanList(this.currentPage);
    },

    /** ⏩ Pagination Controls */
    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.fetchPlanList(this.currentPage);
      }
    },
    async prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.fetchPlanList(this.currentPage);
      }
    },
    async goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        await this.fetchPlanList(this.currentPage);
      }
    },

    /** 🗑️ Delete Plan */
    async deletePlan(plan) {
      if (!confirm(`Delete "${plan.name}"?`)) return;
      this.loading = true;
      this.loadingText = `Deleting ${plan.name}...`;
      try {
        const res = await deletePlan(plan.id);
        if (res.status) await this.fetchPlanList(this.currentPage);
      } catch (err) {
        console.error("deletePlan error:", err);
      } finally {
        this.loading = false;
      }
    },

    editPlan(plan) {
      console.log("Editing plan:", plan.name);
    },
  },
};
</script>



<style scoped>
.plan-card {
  transition: all 0.3s ease;
}
.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 18px rgba(0,0,0,0.08);
}

/* Buttons */
.btn-edit, .btn-delete {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-edit {
  background: #EEF2FF;
  color: var(--primary);
}
.btn-edit:hover {
  background: var(--primary);
  color: #fff;
}
.btn-delete {
  background: #FEE2E2;
  color: #991B1B;
}
.btn-delete:hover {
  background: #DC2626;
  color: #fff;
}

/* Status Colors */
.status.active {
  background: #DCFCE7;
  color: #166534;
}
.status.deactive {
  background: #FEE2E2;
  color: #991B1B;
}

/* Responsive */
@media (max-width: 992px) {
  .plan-card { flex: 0 0 calc(50% - 20px); }
}
@media (max-width: 600px) {
  .plan-card { flex: 0 0 100%; }
}
/* =====================================================
   📱 RESPONSIVE FIX — PRICING & PLANS PAGE
   ===================================================== */

@media (max-width: 1024px) {
  /* Filter Row */
  .filter-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
    margin: 0 0.5rem 1rem 0.5rem !important;
  }

  .filter-row h1 {
    font-size: 1.1rem !important;
  }

  .filter-row div {
    width: 100% !important;
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 8px !important;
  }

  .filter-row select,
  .filter-row button {
    flex: 1 1 48% !important;
    min-width: 150px !important;
    font-size: 13px !important;
    padding: 8px 10px !important;
  }

  /* KPI Cards */
  .kpi-row {
    gap: 16px !important;
    margin-bottom: 18px !important;
  }

  .kpi-card {
    min-width: 100% !important;
    padding: 14px 16px !important;
  }

  /* Plan Cards */
  .plan-grid {
    justify-content: center !important;
    gap: 16px !important;
  }

  .plan-card {
    flex: 0 0 calc(50% - 12px) !important;
    min-width: 260px !important;
    padding: 14px !important;
  }
}

@media (max-width: 768px) {
  /* Filter Adjustments */
  .filter-row {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px !important;
  }

  .filter-row select,
  .filter-row button {
    width: 100% !important;
  }

  /* KPI Cards Stack */
  .kpi-row {
    flex-direction: column !important;
    gap: 14px !important;
  }

  .kpi-card {
    padding: 12px 14px !important;
  }

  /* Plan Grid */
  .plan-grid {
    gap: 14px !important;
    justify-content: center !important;
  }

  .plan-card {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    padding: 14px !important;
    margin: 0 auto !important;
  }

  /* Card Content Adjustments */
  .plan-card h4 {
    font-size: 0.95rem !important;
  }

  .plan-card p {
    font-size: 12.5px !important;
  }

  .plan-footer {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 8px !important;
  }

  /* Pagination */
  .pricing-page > div:last-child {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
    margin-top: 18px !important;
  }

  /* Bottom Padding */
  .pricing-page {
    padding-bottom: 2.5rem !important;
  }
}

@media (max-width: 480px) {
  .filter-row h1 {
    font-size: 1rem !important;
  }

  .filter-row select,
  .filter-row button {
    font-size: 12px !important;
    padding: 6px 10px !important;
  }

  .kpi-card {
    padding: 10px 12px !important;
  }

  .plan-card {
    padding: 12px !important;
  }

  .plan-card p {
    font-size: 12px !important;
    line-height: 1.4 !important;
  }

  .plan-footer button {
    padding: 5px 8px !important;
    font-size: 11px !important;
  }

  .pricing-page {
    padding-bottom: 2rem !important;
  }
}
/* ======================================
   🎬 FADE-SLIDE TRANSITION (for loader)
   ====================================== */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.35s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ======================================
   💎 LIGHT / DARK MODE SUPPORT
   ====================================== */
:root {
  --loader-bg: rgba(255, 255, 255, 0.85);
}

[data-theme="dark"] .global-loader {
  background: rgba(0, 0, 0, 0.55);
}

[data-theme="dark"] .global-loader p {
  color: #f3f4f6;
}
/* ===========================================
   🧩 MODAL POPUP (Add/Edit Plan)
   =========================================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(3px);
}

.modal-card {
  background: var(--card);
  border-radius: 12px;
  width: 95%;
  max-width: 560px;
  padding: 20px 24px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  animation: fadeInUp 0.35s ease;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field.full {
  grid-column: span 2;
}

.form-field label {
  font-size: 13px;
  color: var(--muted);
  font-weight: 500;
}

.form-field input,
.form-field select,
.form-field textarea {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  color: var(--text);
  background: var(--card);
}

.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  outline: none;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

.btn-cancel,
.btn-save {
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-cancel {
  background: #f3f4f6;
  color: var(--text);
}
.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-save {
  background: var(--primary);
  color: #fff;
}
.btn-save:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.25);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>